import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useIntl } from 'umi';
import { Table, TableColumn } from 'tea-component';
import Abnormal from '@/pages/intelligence/components/Abnormal';
import TableCountryIcon from '@/pages/intelligence/components/TableComponents/components/TableCountryIcon';
import { StoreRankDetailsCoreContext } from '../../context';
import { getCountryFlag } from '@/pages/home/_config/countries';
import useCountriesNames from '@intelligence/hooks/useCountriesNames';
import './RankTable.scss';
import type { IBaseProps } from '@/pages/intelligence/types/common';
import type {
  IStoreRankDetailsTablesSourceData,
  IStoreRankDetailsCountriesRanks,
  IStoreRankDetailsCountryRank,
} from '@/pages/intelligence/types/storeRank';
import type { IStoreRankDetailsCoreContext } from '../../types';
import { RANKING_NUMBER_LIST } from './constants/index';
import TableShowData from '@/pages/intelligence/components/TableComponents/components/TableShowData';
import { valueFormat, growthFormat } from '@/pages/intelligence/utils/commonUtils';
interface ISubTableProps extends IBaseProps {
  record: IStoreRankDetailsTablesSourceData;
  ranks?: IStoreRankDetailsCountryRank[];
}

export default function RankTable(props: ISubTableProps) {
  const { formatMessage } = useIntl();

  const { record } = props;

  // console.log('RankTableRankTable', props);

  const { findCountryLocalName } = useCountriesNames();
  const { fetchCountriesRankData } = useContext<IStoreRankDetailsCoreContext>(
    StoreRankDetailsCoreContext,
  );

  const [isLoading, setIsLoading] = useState(true);
  const [tableData, setTableData] = useState<IStoreRankDetailsCountriesRanks[]>([]);

  const columns: TableColumn<IStoreRankDetailsCountriesRanks>[] = (() => {
    const columnsTemp: TableColumn<IStoreRankDetailsCountriesRanks>[] = [
      {
        key: 'ranking',
        header: formatMessage({ id: 'dbi_ranking' }),
        width: 102,
        render: (cvm: any) => {
          return <div className="dbi-store-rank-num">{`#${cvm?.ranking}`}</div>;
        },
      },
      {
        key: 'quantity',
        header: formatMessage({ id: 'dbi_quantity' }),
        width: 100,
        render: (cvm: any) => {
          const value = valueFormat(cvm?.quantity);
          const valueRatio = cvm?.dod;
          const growthDataObj = growthFormat({
            value: valueRatio,
            // value: entityType === 'combine' ? valueRatio : valueRatio * -1,
            valueType: 'rankValue',
            hasPoint: 'none',
            fixedNum: 0,
            maxNumber: 9999,
          });

          return (
            <TableShowData
              maxNumber="9999"
              value={value}
              type={growthDataObj?.type}
              growth={growthDataObj?.value}
            />
          );
        },
      },
      {
        key: 'markets',
        header: formatMessage({ id: 'dbi_country_region' }),
        render: (cvm: any) => {
          return (
            <div className="dbi-country-region-list">
              {cvm?.markets?.map((country: any, index: any) => {
                const value = valueFormat(country?.rank);
                const valueRatio = country?.dod;
                const growthDataObj = growthFormat({
                  value: valueRatio,
                  valueType: 'rankValue',
                  hasPoint: 'none',
                  fixedNum: 0,
                  maxNumber: 9999,
                });

                return (
                  <div key={index} className="dbi-country-region-item">
                    <div className="dbi-country-region-info">
                      <TableCountryIcon
                        cvm={findCountryLocalName(country?.market)}
                        icon={getCountryFlag(country?.market)}
                      />
                    </div>
                    <TableShowData
                      maxNumber="9999"
                      value={`#${value}`}
                      type={growthDataObj?.type}
                      growth={growthDataObj?.value}
                    />
                  </div>
                );
              })}
            </div>
          );
        },
      },
    ];
    return columnsTemp;
  })();

  const getSortTable = (tableList: any) => {
    const sortList: any = [];
    RANKING_NUMBER_LIST.forEach((rank) => {
      const rankObj = tableList?.find((item: any) => item.ranking === rank);

      if (rankObj && Array.isArray(rankObj?.markets) && rankObj?.markets?.length > 0) {
        sortList.push(rankObj);
      }
    });
    return sortList;
  };

  const fetchTableData = async () => {
    setIsLoading(true);
    if (Array.isArray(record?.countryDetail) && record?.countryDetail?.length > 0) {
      setTimeout(() => {
        setTableData(getSortTable(record?.countryDetail));
        setIsLoading(false);
      }, 500);
    } else {
      try {
        const res = await fetchCountriesRankData?.(record);
        const tableDataTemp = Array.isArray(res) ? res : [];
        setTableData(getSortTable(tableDataTemp));
      } catch (err) {
        setTableData([]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // const getTableData = () => {
  //   if (Array.isArray(record?.countryDetail) && record?.countryDetail?.length > 0) {
  //     setTableData(getSortTable(record?.countryDetail));
  //     setIsLoading(false);
  //   } else {
  //     fetchTableData();
  //   }
  // };

  // const isNoDataCur = useMemo(() => {
  //   if (Array.isArray(record?.countryDetail) && record?.countryDetail?.length > 0) {
  //     return false;
  //   } else {
  //     return !isLoading && !tableData?.length;
  //   }
  // }, [record, tableData, isLoading]);

  useEffect(() => {
    fetchTableData();
  }, []);

  return (
    <Abnormal
      style={{ height: 200 }}
      isLoading={isLoading}
      isNoData={!isLoading && !tableData?.length}
      component={
        <Table
          records={tableData}
          bordered={true}
          columns={columns}
          className="dbi-store-country-regin-table"
        />
      }
    />
  );
}
